<template>
    <vx-table-row>
        <vx-table-row-title class="break-words">
            {{ props.target.name }}
        </vx-table-row-title>

        <vx-table-row-item class="min-w-[100px]">
            <template v-slot:label> Type: </template>
            <vx-badge v-if="props.target.type === 'ou'" size="xs" color="violet" data-test="type-badge">
                <font-awesome-icon :icon="faBuildingFlag"></font-awesome-icon>
                OU
            </vx-badge>
            <vx-badge v-else size="xs" color="slate" data-test="type-badge">
                <font-awesome-icon :icon="faGrid2Plus" class="-mx-0.5"></font-awesome-icon>
                Group
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-item class="min-w-[220px]">
            <template v-slot:label> Role: </template>
            <vx-selectable
                v-if="isEditable"
                :modelValue="props.target.role"
                @update:modelValue="onRoleChange"
                :reduce="(option) => option.value"
                :options="roleOptions"
                :clearable="false"
                :disabled="updating"
                data-test="role-select"
                class="w-[200px]"
            />
            <template v-else>
                {{ roleLabel }}
            </template>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="isEditable">
            <vx-button
                data-test="remove"
                aria-label="Remove target access"
                @click="confirmRemove"
                type="button"
                color="muted"
                outline
                class="w-[45px]"
            >
                <font-awesome-icon widthAuto :icon="faCircleMinus" />
            </vx-button>
            <vx-button :href="targetUrl" data-test="go-to-target" color="muted" outline class="w-[125px]">
                Go To Target <font-awesome-icon widthAuto :icon="faSquareArrowUpRight" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faBuildingFlag, faCircleMinus, faGrid2Plus, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxSelectable,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { orgUnitUsersClient, groupUsersClient } from '~/services';
import { orgUnitRolesOptions, groupRolesOptions, ORG_UNIT_USER_ROLES, GROUP_USER_ROLES } from '~/constants/roles';
import { catchRequestError, throwSwalRequestError } from '~/utils/request';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    userId: {
        type: Number,
        required: true,
    },
    target: {
        type: Object,
        required: true,
    },
    editable: {
        type: Boolean,
        default: true,
    },
    isKiosk: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['removed', 'roleUpdated']);

const toasted = useToasted();
const updating = ref(false);
let activeToastId = null;

const isEditable = computed(() => props.editable);

const roleOptions = computed(() => (props.target.type === 'ou' ? orgUnitRolesOptions() : groupRolesOptions()));

const rolesMap = computed(() => (props.target.type === 'ou' ? ORG_UNIT_USER_ROLES : GROUP_USER_ROLES));

const roleLabel = computed(() => rolesMap.value[props.target.role] || props.target.role);

const targetUrl = computed(() => {
    const prefix = props.isKiosk ? '/kiosk' : '/settings';
    if (props.target.type === 'ou') {
        return `${prefix}/teams/${props.teamId}/org-units/${props.target.targetId}`;
    }
    return `${prefix}/teams/${props.teamId}/groups/${props.target.targetId}`;
});

const onRoleChange = async (newRole) => {
    if (newRole === props.target.role || updating.value) {
        return;
    }

    const previousRole = props.target.role;
    updating.value = true;

    try {
        if (props.target.type === 'ou') {
            await orgUnitUsersClient.upsertOrgUnitUser(props.teamId, props.target.targetId, props.userId, {
                role: newRole,
            });
        } else {
            await groupUsersClient.upsertGroupUser(props.teamId, props.target.targetId, props.userId, {
                role: newRole,
            });
        }

        emit('roleUpdated', { ...props.target, role: newRole });

        if (activeToastId !== null) {
            toasted.dismiss(activeToastId);
        }

        const toastConfig = {
            title: `${props.target.name} role updated to ${rolesMap.value[newRole]}`,
            type: 'success',
        };

        if (newRole !== 'owner') {
            toastConfig.action = {
                label: 'Undo',
                onClick: async () => {
                    toasted.dismiss(activeToastId);
                    activeToastId = null;
                    try {
                        if (props.target.type === 'ou') {
                            await orgUnitUsersClient.upsertOrgUnitUser(
                                props.teamId,
                                props.target.targetId,
                                props.userId,
                                { role: previousRole }
                            );
                        } else {
                            await groupUsersClient.upsertGroupUser(props.teamId, props.target.targetId, props.userId, {
                                role: previousRole,
                            });
                        }
                        emit('roleUpdated', { ...props.target, role: previousRole });
                    } catch (e) {
                        toasted.error(catchRequestError(e));
                    }
                },
            };
        }

        activeToastId = toasted.show(toastConfig);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        updating.value = false;
    }
};

const confirmRemove = () => {
    return VxSwal.confirmation(
        'Remove Target Access',
        'The user will immediately lose access to this target.',
        'danger',
        () => {
            const request =
                props.target.type === 'ou'
                    ? orgUnitUsersClient.deleteOrgUnitUser(props.teamId, props.target.targetId, props.userId)
                    : groupUsersClient.deleteGroupUser(props.teamId, props.target.targetId, props.userId);

            return request
                .then(() => {
                    emit('removed', props.target);
                    toasted.success(`${props.target.name} removed`);
                })
                .catch(throwSwalRequestError);
        }
    );
};
</script>
